import React, { ReactNode, useEffect } from 'react'
import flowRight from 'lodash/flowRight'
import { useDispatch, useSelector } from 'react-redux'
import { showMinimizeDialog, UserScreening } from 'shared/redux/app/reducer'
import { createSelector } from '@reduxjs/toolkit'
import Sidebar from './Sidebar'
import { GlobalState } from 'shared/redux/reducer'
import Button from 'shared/components/Button'
import { SVGIcon } from 'react-md'
import styled from 'styled-components'
import Box from '../Box'
import { useAtBreakpoints } from 'shared/hooks/useBreakpoint'
import withAuth, {
  roleSelector,
  authSelector,
  screeningSelector,
  organizationSettingSelector,
} from 'shared/hocs/withAuth'
import useScreeningActions, { PARENT_DIALOG, PARTICIPANT_DIALOG } from 'shared/components/Screening/useScreeningActions'

const pageSelector = createSelector(
  (state: GlobalState) => state.app.toast,
  (state: GlobalState) => state.app.dialog,
  (state: GlobalState) => state.app.drawer,
  (state: GlobalState) => state.app.temporaryClosedDialogs,
  (state: GlobalState) => state.app.minimizeDialogs,
  (state: GlobalState) => state.app.mobileMenu,
  (toast, dialog, drawer, temporaryClosedDialogs, minimizeDialogs, mobileMenu) => ({
    toast,
    dialog,
    drawer,
    hasTemporaryClosed: temporaryClosedDialogs.length > 0,
    minimizeDialogs,
    mobileMenu,
  }),
)

type PageProps = {
  children: ReactNode
  hasNavigatin?: boolean
  pageId: string
  hasNavigation: boolean
  pageTitle?: string
  requiredRoles?: string[]
}

function Page(props: PageProps) {
  const { children, hasNavigation = false, pageId = 'page' } = props
  const { organization_information } = useSelector(organizationSettingSelector)
  const { isScreeningRequested, screening } = useSelector(screeningSelector)
  const appData = useSelector(pageSelector)
  const dispatch = useDispatch()
  const isLarge = useAtBreakpoints(['tablet-ipad-landscape', 'desktop'])
  const { dialog, drawer, minimizeDialogs = [], mobileMenu } = appData

  const DialogComponent = dialog?.component
  const MobileMenuComponent = mobileMenu?.component

  const { handleShowFollowupDialog, handleClickInitialConfirmation } = useScreeningActions()

  useEffect(() => {
    const [screeningUser] = screening?.users
    const showScreening = screening?.users?.some((user: UserScreening) => user?.showScreening)
    const hasFlag = screening?.users?.some((user: UserScreening) => user?.isFlagged)
    const allComplete = screening?.users?.every((user: UserScreening) => !user?.showScreening)
    if (!['reset-password', 'view-waiver'].includes(pageId)) {
      if (!isScreeningRequested && screening?.users?.length > 1) {
        if (showScreening) {
          handleClickInitialConfirmation(PARENT_DIALOG, screening, true)()
        }

        if (allComplete && hasFlag) handleShowFollowupDialog()
      }

      if (!isScreeningRequested && showScreening && screening?.users?.length > 1) {
        handleClickInitialConfirmation(PARENT_DIALOG, screening, true)()
      }

      if (!isScreeningRequested && screening?.users?.length === 1) {
        if (showScreening) handleClickInitialConfirmation(PARTICIPANT_DIALOG, screening, true)()

        if (screeningUser?.isFlagged) handleShowFollowupDialog()
      }
    }
  }, [isScreeningRequested, screening])

  let DrawerComponent
  if (drawer && drawer.component) {
    ;({ component: DrawerComponent } = drawer)
  }

  let { pageTitle } = props
  if (pageTitle) {
    pageTitle = `${organization_information.name} - ${pageTitle}`
  } else {
    pageTitle = organization_information.name
  }

  return (
    <>
      {DialogComponent && dialog && <DialogComponent {...dialog.props} />}
      {DrawerComponent && drawer && <DrawerComponent {...drawer.props} />}
      {MobileMenuComponent && mobileMenu && <MobileMenuComponent {...mobileMenu.props} />}
      <MainLayout id={`page-${pageId}`}>
        {hasNavigation && <Sidebar />}
        <Wrapper id="wrapper" $offsetWrapper={hasNavigation && isLarge}>
          {children}
        </Wrapper>
        <div className="minimize-dialogs">
          {[...minimizeDialogs].reverse().map((each: any, index: number) => (
            <Button key={index} onClick={() => dispatch(showMinimizeDialog(each.minimizeName))} variant="primary">
              {each.minimizeName}
              <SVGIcon aria-label="maximize">
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M3 3h18v2H3z" />
              </SVGIcon>
            </Button>
          ))}
        </div>
      </MainLayout>
    </>
  )
}

const MainLayout = styled.main``

const Wrapper = styled(Box)<{ $offsetWrapper: boolean }>`
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    background-color: #f5f5f5;
    border-radius: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.label};
    border-radius: 50px;
  }

  ${({ $offsetWrapper }) =>
    $offsetWrapper &&
    `
    display: block;
    overflow: auto;
    margin-left: 320px;
    // height: 100vh;
    transition: margin-left 0.5s;
    position: relative;
  `}
`

const EnhancedPage = flowRight(withAuth)(Page)

Page.defaultProps = {
  isKioskAllowed: false,
  hasNavigation: true,
  pageId: '',
  requiredRoles: [],
}

EnhancedPage.defaultProps = Page.defaultProps

export default EnhancedPage
